/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message/message';
import { token } from '@atlaskit/tokens';
import { type ExperienceNameType } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { ExperienceFailure } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';

const styles = cssMap({
	wrapper: {
		height: '100%',
		width: '100%',
		backgroundColor: token('color.background.danger'),
	},
});

type ErrorViewProps = { error: Error };

const ErrorView = ({ error }: ErrorViewProps) => {
	return (
		<Fragment>
			<Inline
				alignBlock="center"
				alignInline="center"
				xcss={styles.wrapper}
				testId="ai-mate-error-boundary"
			>
				<SectionMessage title="Oops. We've got an error." appearance="error">
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{error?.message ? <Text as="p">[{error.message}]</Text> : null}
				</SectionMessage>
			</Inline>
		</Fragment>
	);
};

type ErrorBoundaryState = {
	error?: Error;
};

// eslint-disable-next-line @repo/internal/react/no-class-components
export class ErrorBoundary extends React.Component<
	React.PropsWithChildren<{
		experienceName?: ExperienceNameType;
		experienceNameList?: ExperienceNameType[];
		rethrowErrors?: boolean;
		fallback?: React.ReactNode;
	}>,
	ErrorBoundaryState
> {
	static displayName = 'ConversationAssistantErrorBoundary';

	public state: ErrorBoundaryState = {};

	componentDidCatch(error: Error): void {
		addSentryBreadcrumb(`ErrorBoundary: componentDidCatch: ${error.message}`);
		this.setState({ error });
		if (this.props.rethrowErrors) {
			throw error;
		}
	}

	render(): string | number | boolean | Iterable<React.ReactNode> | JSX.Element | null | undefined {
		const { error } = this.state;
		const { children, experienceName, experienceNameList } = this.props;

		if (!!error) {
			const combinedExperienceNameList: ExperienceNameType[] = [
				...new Set([...(experienceNameList || []), experienceName]),
			].filter(
				(experienceName): experienceName is ExperienceNameType => experienceName !== undefined,
			);
			return (
				<Fragment>
					{this.props.fallback ? this.props.fallback : <ErrorView error={error} />}
					{combinedExperienceNameList.map((experienceName) => (
						<ExperienceFailure error={error} name={experienceName} />
					))}
				</Fragment>
			);
		}

		return children;
	}
}
